import {
  FormSubmitButton,
  FormTextField,
  FormProvider,
  useForm,
} from '@mfw/forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntl, Button } from '@mfw/components';
import { config } from '@mfw/translations';
import Box from '@mui/material/Box';

import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';
import type { UiUserAddressStep } from '../../../../../schemas/users.schema';

import {
  useAddressContext,
  AddressProvider,
  FormAddress,
} from '../../../../../allFeatures/FormAddress';
import { UiUserAddressStepSchema } from '../../../../../schemas/users.schema';
import { DEFAULT_COUNTRY_CODE } from '../../../../../schemas/address.schema';
import { useUpdateUser } from '../../../../../hooks/usersv2';
import { FormPhoneNumber } from '../../../../../components';
import OnboardingHeader from '../../../OnboardingHeader';
import { useAuthContext } from '../../../../../context';
import { useWizard } from '../../../../../allFeatures';
import generalMessages from '../../../../../messages';
import messages from '../BasicInfoStep/messages';

const ADDRESS_OPTIONAL_FIELDS = {
  city: true,
  country: false,
  street: true,
  zip: true,
};

const BasicInfoDialogStepContent = () => {
  const { formatMessage } = useIntl();
  const { currentUser } = useAuthContext();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const { mutateAsync: updateUserV2Async } = useUpdateUser();
  const { isPending: isPendingAddress } = useAddressContext();

  const methods = useForm({
    defaultValues: {
      address: {
        city: currentUser?.address?.city ?? null,
        country: currentUser?.address?.country ?? [
          { value: DEFAULT_COUNTRY_CODE },
        ],
        isAddressPublic: currentUser?.address?.isAddressPublic ?? false,
        street: currentUser?.address?.street ?? null,
        zip: currentUser?.address?.zip ?? null,
      },
      contact: {
        isPhoneNumberPublic: currentUser?.contact?.isPhoneNumberPublic ?? false,
        phoneCountryCode:
          currentUser?.contact?.phoneCountryCode ?? config.defaultCountryCode,
        phoneNumber: currentUser?.contact?.phoneNumber ?? '',
      },
      email: currentUser?.email || '',
      name: currentUser?.name || '',
    } satisfies UiUserAddressStep,
    resolver: zodResolver(UiUserAddressStepSchema),
  });

  const handleNext = async (data: UiUserAddressStep) => {
    await updateUserV2Async(data);
    goToStep('about');
  };

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={<p>{formatMessage(messages.description)}</p>}
          title={formatMessage(messages.title)}
        />
      </Box>
      <Box flexDirection="column" display="flex" gap={2} px={3} py={2}>
        <FormTextField
          label={formatMessage(messages.name)}
          data-testid="client-onboarding-name"
          name="name"
        />
        <FormTextField
          helperText={formatMessage(messages.emailHelper)}
          label={formatMessage(messages.email)}
          data-testid="client-onboarding-email"
          className="my-4"
          name="email"
          type="email"
          disabled
        />
        <FormPhoneNumber
          phoneNumberPublic="contact.isPhoneNumberPublic"
          phoneCountryCodeName="contact.phoneCountryCode"
          label={formatMessage(messages.phone)}
          qa="trainer-basic-info__phone-number"
          phoneNumberName="contact.phoneNumber"
          data-testid="client-onboarding-phone"
        />
        <FormAddress
          data-testid="client-onboarding-address"
          optional={ADDRESS_OPTIONAL_FIELDS}
          name="address"
        />
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          onClick={() => goToStep('timezoneLanguage')}
          data-testid="onboarding-back"
          variant="outlined"
          size="large"
        >
          {formatMessage(generalMessages.back)}
        </Button>
        <FormSubmitButton
          data-testid="onboarding-next"
          disabled={isPendingAddress}
          variant="contained"
          disableIfNotDirty
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};

export const BasicInfoDialogStep = () => {
  return (
    <AddressProvider>
      <BasicInfoDialogStepContent />
    </AddressProvider>
  );
};
